import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { communityKeys, communityPostKeys } from 'modules/community/api/key-factory'
import type { ProfileInfo } from 'modules/community/types/member-interface'
import {
  UpdateUserDomainAccountSettingsPayload,
  UpdateUserSettingsPayload,
  UseUserSettingsInterface,
} from '../types/user-settings-interface'

type UpdateUserSettingsBody = UpdateUserDomainAccountSettingsPayload | UpdateUserSettingsPayload

// Keeps cached community profile info (header avatars, mentions) in sync after a save.
function patchProfileInfoCache(
  current: ProfileInfo | undefined,
  displayName: string | null,
  profileImage: UseUserSettingsInterface['userDomainAccount']['profileImage'],
): ProfileInfo | undefined {
  if (!current) {
    return current
  }
  const next: ProfileInfo = { ...current, displayName: displayName ?? current.displayName }
  if (profileImage?.path) {
    next.profileImageUrl = profileImage.path
  }
  return next
}

export const useUpdateUserSettings = (
  options: Omit<
    UseMutationOptions<UseUserSettingsInterface, Error, UpdateUserSettingsBody>,
    'mutationKey' | 'mutationFn'
  > = {},
): UseMutationResult<UseUserSettingsInterface, Error, UpdateUserSettingsBody, unknown> => {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.patch,
    UseUserSettingsInterface,
    UpdateUserSettingsBody
  >({
    method: RequestMethodsEnum.patch,
  })

  return useMutation({
    mutationFn: (data: UpdateUserSettingsBody) => fetcher('/api/settings/profile', data),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      const { displayName, profileImage } = data.userDomainAccount
      if (isAuthorizedUser(user) && displayName != null) {
        queryClient.setQueriesData<ProfileInfo>(
          { queryKey: [...communityKeys.communityGeneral(), '/get-profile-info'] },
          current => patchProfileInfoCache(current, displayName, profileImage),
        )
        await queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
      }
      await options.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
